import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Lock, Eye, EyeOff, AlertCircle, CheckCircle2, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password.length < 8) {
      setError('Le mot de passe doit contenir au moins 8 caractères');
      return;
    }
    if (password !== confirmPassword) {
      setError('Les mots de passe ne correspondent pas');
      return;
    }
    if (!token) {
      setError('Token de réinitialisation manquant. Veuillez utiliser le lien reçu par email.');
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`${API}/api/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, password }),
      });

      if (res.ok) {
        setSuccess(true);
        toast.success('Mot de passe réinitialisé avec succès');
      } else {
        const cloned = res.clone();
        try {
          const data = await cloned.json();
          setError(data.detail || 'Erreur lors de la réinitialisation');
        } catch {
          setError('Erreur lors de la réinitialisation');
        }
      }
    } catch {
      setError('Erreur de connexion. Veuillez réessayer.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left side - Form */}
      <div className="flex-1 flex flex-col justify-center px-8 md:px-16 lg:px-24 bg-white">
        <div className="max-w-md w-full mx-auto">
          <div className="flex items-center gap-3 mb-10">
            <img src="/affea-logo.png" alt="AFFEA" className="w-14 h-14 rounded-lg object-cover" />
            <div>
              <h1 className="font-display text-2xl font-bold text-navy-700">AFFEA</h1>
              <p className="text-sm text-teal-600 italic">Académie Francophone de la Formation, de l'Enseignement et de l'Accompagnement</p>
            </div>
          </div>

          {success ? (
            <div className="text-center" data-testid="reset-success">
              <div className="w-16 h-16 mx-auto mb-5 rounded-full bg-emerald-100 flex items-center justify-center">
                <CheckCircle2 className="h-8 w-8 text-emerald-600" />
              </div>
              <h2 className="font-display text-2xl font-bold text-slate-900 mb-3">
                Mot de passe réinitialisé
              </h2>
              <p className="text-slate-500 mb-8 text-sm leading-relaxed">
                Votre mot de passe a été modifié avec succès. Vous pouvez maintenant vous connecter avec votre nouveau mot de passe.
              </p>
              <Button
                onClick={() => navigate('/login')}
                className="bg-gradient-to-r from-teal-600 to-emerald-600 text-white px-8"
                data-testid="go-to-login-btn"
              >
                Aller à la page de connexion
              </Button>
            </div>
          ) : (
            <>
              <button
                onClick={() => navigate('/login')}
                className="flex items-center gap-2 text-sm text-slate-500 hover:text-slate-700 mb-6 transition-colors"
                data-testid="reset-back-to-login"
              >
                <ArrowLeft className="h-4 w-4" /> Retour à la connexion
              </button>

              <h2 className="font-display text-2xl font-bold text-navy-700 mb-2 italic" data-testid="reset-title">
                Nouveau mot de passe
              </h2>
              <p className="text-slate-600 mb-6 text-sm">
                Choisissez un nouveau mot de passe pour votre compte. Il doit contenir au moins 8 caractères.
              </p>

              <form onSubmit={handleSubmit} className="space-y-4" data-testid="reset-password-form">
                <div>
                  <Label htmlFor="new-password" className="text-sm text-slate-600">Nouveau mot de passe</Label>
                  <div className="relative mt-1">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input
                      id="new-password"
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={e => { setPassword(e.target.value); setError(''); }}
                      placeholder="Minimum 8 caractères"
                      className="pl-10 pr-10"
                      autoFocus
                      required
                      minLength={8}
                      data-testid="reset-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>

                <div>
                  <Label htmlFor="confirm-password" className="text-sm text-slate-600">Confirmer le mot de passe</Label>
                  <div className="relative mt-1">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input
                      id="confirm-password"
                      type={showPassword ? 'text' : 'password'}
                      value={confirmPassword}
                      onChange={e => { setConfirmPassword(e.target.value); setError(''); }}
                      placeholder="Confirmez votre mot de passe"
                      className="pl-10"
                      required
                      minLength={8}
                      data-testid="reset-confirm-input"
                    />
                  </div>
                </div>

                {/* Password strength indicators */}
                <div className="space-y-1.5">
                  <div className="flex items-center gap-2">
                    <div className={`w-1.5 h-1.5 rounded-full ${password.length >= 8 ? 'bg-emerald-500' : 'bg-slate-300'}`} />
                    <span className={`text-xs ${password.length >= 8 ? 'text-emerald-600' : 'text-slate-400'}`}>
                      Au moins 8 caractères
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className={`w-1.5 h-1.5 rounded-full ${password && password === confirmPassword ? 'bg-emerald-500' : 'bg-slate-300'}`} />
                    <span className={`text-xs ${password && password === confirmPassword ? 'text-emerald-600' : 'text-slate-400'}`}>
                      Les mots de passe correspondent
                    </span>
                  </div>
                </div>

                {error && (
                  <div className="flex items-start gap-2 p-3 rounded-lg bg-red-50 border border-red-100" data-testid="reset-error">
                    <AlertCircle className="h-4 w-4 text-red-500 mt-0.5 shrink-0" />
                    <p className="text-sm text-red-600">{error}</p>
                  </div>
                )}

                <Button
                  type="submit"
                  size="lg"
                  className="w-full bg-gradient-to-r from-teal-600 to-emerald-600 text-white py-6"
                  disabled={loading || password.length < 8 || password !== confirmPassword}
                  data-testid="reset-submit-btn"
                >
                  {loading ? 'Réinitialisation en cours...' : 'Réinitialiser le mot de passe'}
                </Button>
              </form>
            </>
          )}
        </div>
      </div>

      {/* Right side - Branded panel */}
      <div className="hidden lg:flex flex-1 relative" style={{ background: 'linear-gradient(135deg, #0a3d62 0%, #0c2d48 40%, #38ada9 100%)' }}>
        <div className="absolute inset-0 bg-gradient-to-br from-transparent to-black/20 z-10" />
        <div className="relative z-20 flex flex-col justify-center items-center p-16 w-full">
          <img src="/affea-logo.png" alt="AFFEA" className="w-28 h-28 rounded-2xl object-cover shadow-2xl mb-8" />
          <h2 className="font-display text-4xl font-bold text-white italic text-center mb-4">
            Plateforme d'Apprentissage
          </h2>
          <p className="text-white/80 text-center text-lg max-w-md">
            Développez vos compétences professionnelles avec AFFEA
          </p>
        </div>
      </div>
    </div>
  );
}
